import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useParams } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import {
  useCreateGiftcardMutation,
  useGiftcard,
  useUpdateGiftcardMutation,
} from '../stores/FetcherStores/GiftcardFetcher';
import { Money } from '../utils/currency';
import { useAlertStore } from '../stores';

interface EditGiftcardProps {
  giftcardId?: string;
  open: boolean;
  onClose: () => void;
}

const EditGiftcard = observer(({ giftcardId, open, onClose }: EditGiftcardProps) => {
  const params = useParams() as { companyKey: string };
  const companyId = params.companyKey;
  const { data: giftcard } = useGiftcard(companyId, giftcardId);
  const { mutateAsync: updateGiftcard, isLoading: isUpdateLoading } = useUpdateGiftcardMutation();
  const { mutateAsync: createGiftcard, isLoading: isCreateLoading } = useCreateGiftcardMutation();
  const alertStore = useAlertStore();
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      title: giftcard?.title || 'Presentkort',
      amount: `${new Money(giftcard?.amount || 0).asDecimal()}`,
      cardNumber: giftcard?.cardNumber || '',
      expiryDate: giftcard?.expiryDate ? DateTime.fromISO(giftcard?.expiryDate) : null,
    },
    onSubmit: async ({ value }) => {
      const giftcardData = {
        ...value,
        amount: new Money(Math.round(Number.parseFloat(value.amount) * 100)),
        expiryDate: value.expiryDate?.toISODate() || new Date().toISOString(),
      };
      let promise;
      if (giftcard?.id) {
        promise = updateGiftcard(companyId, giftcard.id, giftcardData);
      } else {
        promise = createGiftcard(companyId, giftcardData);
      }
      await promise
        .then(() => {
          alertStore.pushAlert({
            message: 'Presentkortet har uppdaterats',
            severity: 'success',
          });
        })
        .catch(() => {
          alertStore.pushAlert({
            message: 'Ett fel uppstod',
            severity: 'error',
          });
        });
      onClose();
    },
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          width: 600,
          height: '100%',
          paddingRight: 4,
          paddingLeft: 4,
        }}
      >
        <Toolbar />
        <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={onClose}>
            <ChevronRightIcon />
          </IconButton>
        </Toolbar>
        <Typography variant="h4" paragraph gutterBottom>
          Ändra presentkort
        </Typography>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}
        >
          <Grid container spacing={2}>
            <form.Field
              name="title"
              validators={{
                onChange: ({ value }) => {
                  if (!value) {
                    return 'Du måste ange ett namn';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Grid item xs={12}>
                  <TextField
                    id="productName"
                    label="Namn"
                    variant="outlined"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    fullWidth
                    error={field.state.meta.errors.length > 0}
                    helperText={field.state.meta.errors.join(', ')}
                  />
                </Grid>
              )}
            </form.Field>
            <form.Field
              name="cardNumber"
              validators={{
                onChange: ({ value }) => {
                  if (!value) {
                    return 'Du måste ange ett kortnummer';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Grid item xs={12}>
                  <TextField
                    id="cardNumber"
                    label="Kortnummer"
                    variant="outlined"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    fullWidth
                    error={field.state.meta.errors.length > 0}
                    helperText={field.state.meta.errors.join(', ')}
                  />
                </Grid>
              )}
            </form.Field>
            <form.Field
              name="amount"
              validators={{
                onChange: ({ value }) => {
                  if (!value) {
                    return 'Du måste ange ett belopp';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Grid item xs={12}>
                  <TextField
                    id="amount"
                    label="Tillgängligt belopp"
                    variant="outlined"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    fullWidth
                    error={field.state.meta.errors.length > 0}
                    helperText={field.state.meta.errors.join(', ')}
                  />
                </Grid>
              )}
            </form.Field>
            <form.Field
              name="expiryDate"
              validators={{
                onChange: (value) => {
                  if (!value) {
                    return 'Du måste ange ett datum';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Grid item xs={12}>
                  <DatePicker
                    sx={{ width: '100%' }}
                    onChange={(date) => field.handleChange(date)}
                    value={field.state.value}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        helperText: field.state.meta.errors.join(', '),
                        error: field.state.meta.errors.length > 0,
                      },
                    }}
                  />
                </Grid>
              )}
            </form.Field>
          </Grid>

          <Box
            sx={{
              marginTop: 'auto',
              marginLeft: 'auto',
              padding: '1rem 0',
            }}
          >
            <Button variant="contained" type="submit" disabled={isUpdateLoading || isCreateLoading}>
              {t('productScreen.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
});

export default EditGiftcard;
